import { useState, useCallback, useRef, useEffect, useLayoutEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CaptureCountdown } from "./CaptureCountdown";
import { CropOverlay } from "./CropOverlay";
import { useScreenCapture } from "@/hooks/useScreenCapture";
import { useCaptureCountdown } from "@/hooks/useCaptureCountdown";
import { useOcr } from "@/hooks/useOcr";
import { useCropSelection } from "@/hooks/useCropSelection";
import { useRedactionBrush } from "@/hooks/useRedactionBrush";
import { getScreenCaptureMeta } from "@/lib/screenCapture";
import { SCREEN_CAPTURE_SUGGESTIONS } from "@/types/screenCapture";
import { Crop, Eraser, Type, Loader2, Monitor } from "lucide-react";

/** Draws image at given size and passes canvas to parent when ready. */
function RedactImageCanvas({
  src,
  width,
  height,
  onReady,
}: {
  src: string;
  width: number;
  height: number;
  onReady: (canvas: HTMLCanvasElement | null) => void;
}) {
  const ref = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const canvas = ref.current;
    if (!canvas || !src) {
      onReady(null);
      return;
    }
    canvas.width = width;
    canvas.height = height;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = canvas.getContext("2d");
      if (ctx) ctx.drawImage(img, 0, 0, width, height);
      onReady(canvas);
    };
    img.onerror = () => onReady(null);
    img.src = src;
    return () => onReady(null);
  }, [src, width, height, onReady]);
  return (
    <canvas
      ref={ref}
      width={width}
      height={height}
      className="absolute left-0 top-0 max-h-[50vh] w-auto object-contain"
      style={{ maxWidth: "100%" }}
    />
  );
}

export type CaptureMode = "image" | "text";

export interface ScreenCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with data URL and meta when user confirms image capture. */
  onCapture: (dataUrl: string, meta: ReturnType<typeof getScreenCaptureMeta>) => void;
  /** Called with extracted text when user chose "Capture as text". */
  onCaptureText?: (text: string) => void;
  /** Optional: suggest prompts when image is attached (for parent to set input). */
  onSuggestPrompt?: (prompt: string) => void;
  /** Countdown seconds before capture (3–5). 0 = no countdown. */
  countdownSeconds?: number;
}

export function ScreenCaptureDialog({
  open,
  onOpenChange,
  onCapture,
  onCaptureText,
  onSuggestPrompt,
  countdownSeconds = 2,
}: ScreenCaptureDialogProps) {
  const [phase, setPhase] = useState<"idle" | "countdown" | "capturing" | "preview" | "ocr">("idle");
  const [captureAsText, setCaptureAsText] = useState(false);
  const [capturedDataUrl, setCapturedDataUrl] = useState<string | null>(null);
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });
  const [isDrawingRedact, setIsDrawingRedact] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const flashOverlayRef = useRef<HTMLDivElement>(null);

  const { capture, isCapturing, error: captureError } = useScreenCapture({
    maxWidth: 1024,
    highDpi: true,
  });

  const { secondsLeft, start: startCountdown, cancel: cancelCountdown, isActive } = useCaptureCountdown({
    duration: countdownSeconds,
    onComplete: () => {
      setPhase("capturing");
    },
    onCancel: () => {
      setPhase("idle");
    },
  });

  const { extractText, isExtracting } = useOcr({ formatCode: true });
  const { selection, start: startCrop, move, end, clear: clearCrop, getCroppedDataUrl } = useCropSelection();
  const {
    isRedacting,
    setIsRedacting,
    setCanvas,
    setOverlay,
    addBlurAt,
    getRedactedDataUrl,
    clearOverlay,
  } = useRedactionBrush();

  const [captureErrorText, setCaptureErrorText] = useState<string | null>(null);

  const runCapture = useCallback(async () => {
    setCaptureErrorText(null);
    try {
      const dataUrl = await capture();
      setCapturedDataUrl(dataUrl);
      if (captureAsText) {
        setPhase("ocr");
        return;
      }
      setPhase("preview");
      if (containerRef.current) {
        setDisplaySize({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : "Capture failed";
      setCaptureErrorText(message);
      setPhase("idle");
    }
  }, [capture, captureAsText]);

  useEffect(() => {
    if (phase === "capturing") {
      runCapture();
    }
  }, [phase, runCapture]);

  useEffect(() => {
    if (phase === "ocr" && capturedDataUrl) {
      let cancelled = false;
      extractText(capturedDataUrl)
        .then((result) => {
          if (!cancelled && onCaptureText) onCaptureText(result.text);
          if (!cancelled) onOpenChange(false);
          setPhase("idle");
          setCapturedDataUrl(null);
        })
        .catch(() => {
          if (!cancelled) setPhase("preview");
        });
      return () => {
        cancelled = true;
      };
    }
  }, [phase, capturedDataUrl, extractText, onCaptureText, onOpenChange]);

  const handleStart = useCallback(() => {
    setCapturedDataUrl(null);
    setCaptureErrorText(null);
    clearCrop();
    clearOverlay();
    if (countdownSeconds > 0) {
      setPhase("countdown");
      startCountdown();
    } else {
      setPhase("capturing");
    }
  }, [countdownSeconds, startCountdown, clearCrop, clearOverlay]);

  const triggerFlash = useCallback(() => {
    if (flashOverlayRef.current) {
      flashOverlayRef.current.classList.add("animate-flash");
      const t = setTimeout(() => flashOverlayRef.current?.classList.remove("animate-flash"), 400);
      return () => clearTimeout(t);
    }
    try {
      const audio = new Audio();
      audio.src = "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQQAAAAB";
      audio.play().catch(() => {});
    } catch {
      // ignore
    }
  }, []);

  const handleUseImage = useCallback(async () => {
    if (!capturedDataUrl) return;
    let finalUrl = capturedDataUrl;
    if (selection && selection.width >= 4 && selection.height >= 4 && containerRef.current) {
      const cropped = await getCroppedDataUrl(capturedDataUrl, {
        width: containerRef.current.offsetWidth,
        height: containerRef.current.offsetHeight,
      });
      if (cropped) finalUrl = cropped;
    }
    if (isRedacting) {
      const redacted = await getRedactedDataUrl();
      if (redacted) finalUrl = redacted;
    }
    triggerFlash();
    const meta = getScreenCaptureMeta();
    onCapture(finalUrl, meta);
    if (onSuggestPrompt && SCREEN_CAPTURE_SUGGESTIONS[0]) {
      onSuggestPrompt(SCREEN_CAPTURE_SUGGESTIONS[0]);
    }
    onOpenChange(false);
    setPhase("idle");
    setCapturedDataUrl(null);
    clearCrop();
    clearOverlay();
  }, [
    capturedDataUrl,
    selection,
    getCroppedDataUrl,
    isRedacting,
    getRedactedDataUrl,
    triggerFlash,
    onCapture,
    onSuggestPrompt,
    onOpenChange,
    clearCrop,
    clearOverlay,
  ]);

  const handleCancel = useCallback(() => {
    cancelCountdown();
    setPhase("idle");
    setCapturedDataUrl(null);
    onOpenChange(false);
  }, [cancelCountdown, onOpenChange]);

  const handleDialogOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        cancelCountdown();
        setPhase("idle");
        setCapturedDataUrl(null);
      }
      onOpenChange(next);
    },
    [cancelCountdown, onOpenChange]
  );

  return (
    <Dialog open={open} onOpenChange={handleDialogOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] flex flex-col"
        onPointerDownOutside={(e) => {
          if (phase === "countdown" || phase === "capturing") e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Monitor className="h-5 w-5" />
            Screen capture
          </DialogTitle>
          <DialogDescription>
            Capture your screen to share with the AI. Choose full screen, a window, or a tab.
          </DialogDescription>
        </DialogHeader>

        {phase === "idle" && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={captureAsText}
                onCheckedChange={(v) => setCaptureAsText(Boolean(v))}
              />
              <span className="text-sm">Capture as text (OCR) — no image sent</span>
            </label>
            <div className="flex gap-2">
              <Button onClick={handleStart} disabled={isCapturing}>
                {isCapturing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Capturing…
                  </>
                ) : (
                  "Start capture"
                )}
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </div>
            {captureError && (
              <p className="text-sm text-destructive">{captureError}</p>
            )}
          </div>
        )}

        {(phase === "countdown" || phase === "capturing") && (
          <div className="flex min-h-[200px] items-center justify-center py-6">
            {phase === "countdown" && (
              <CaptureCountdown
                secondsLeft={secondsLeft}
                onCancel={cancelCountdown}
              />
            )}
            {phase === "capturing" && !capturedDataUrl && (
              <div className="flex flex-col items-center gap-4 text-center max-w-sm">
                <Loader2 className="h-10 w-10 animate-spin text-primary shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-foreground">Choose what to share</p>
                  <p className="text-sm text-muted-foreground">
                    Your browser should show a picker to select <strong>entire screen</strong>, a <strong>window</strong>, or a <strong>tab</strong>. If you don’t see it, check another window or the tab bar — it may be behind this dialog.
                  </p>
                </div>
                {captureErrorText && (
                  <p className="text-sm text-destructive">{captureErrorText}</p>
                )}
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            )}
          </div>
        )}

        {phase === "ocr" && (
          <div className="flex flex-col items-center gap-2 py-8">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Extracting text…</p>
          </div>
        )}

        {phase === "preview" && capturedDataUrl && (
          <div className="flex flex-1 flex-col gap-4 min-h-0">
            <div
              ref={containerRef}
              className="relative flex max-h-[50vh] min-h-[200px] items-center justify-center overflow-hidden rounded-lg border bg-muted/30"
              onMouseMove={isRedacting && isDrawingRedact ? (e) => addBlurAt(e.clientX, e.clientY) : undefined}
              onMouseDown={isRedacting ? (e) => { if (e.button === 0) { setIsDrawingRedact(true); addBlurAt(e.clientX, e.clientY); } } : undefined}
              onMouseUp={isRedacting ? () => setIsDrawingRedact(false) : undefined}
              onMouseLeave={isRedacting ? () => setIsDrawingRedact(false) : undefined}
            >
              <div
                ref={flashOverlayRef}
                className="pointer-events-none absolute inset-0 z-20 bg-white opacity-0"
                aria-hidden
              />
              <img
                ref={imageRef}
                src={capturedDataUrl}
                alt="Captured screen"
                className={`max-h-[50vh] w-auto object-contain ${isRedacting ? "sr-only" : ""}`}
                onLoad={() => {
                  if (containerRef.current) {
                    setDisplaySize({
                      width: containerRef.current.offsetWidth,
                      height: containerRef.current.offsetHeight,
                    });
                  }
                }}
                style={{ maxWidth: "100%" }}
              />
              {isRedacting && displaySize.width > 0 && displaySize.height > 0 && (
                <>
                  <RedactImageCanvas
                    src={capturedDataUrl}
                    width={displaySize.width}
                    height={displaySize.height}
                    onReady={setCanvas}
                  />
                  <canvas
                    ref={(el) => {
                      if (el) {
                        el.width = displaySize.width;
                        el.height = displaySize.height;
                        setOverlay(el);
                      }
                    }}
                    width={displaySize.width}
                    height={displaySize.height}
                    className="absolute left-0 top-0 cursor-crosshair"
                    style={{ maxWidth: "100%" }}
                  />
                </>
              )}
              {!isRedacting && (
                <CropOverlay
                  selection={selection}
                  containerRef={containerRef}
                  onMouseDown={startCrop}
                  onMouseMove={move}
                  onMouseUp={end}
                  onMouseLeave={end}
                />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={!isRedacting ? "secondary" : "outline"}
                size="sm"
                onClick={() => {
                  setIsRedacting(false);
                  clearOverlay();
                }}
              >
                <Crop className="h-4 w-4 mr-1" />
                Crop
              </Button>
              <Button
                variant={isRedacting ? "secondary" : "outline"}
                size="sm"
                onClick={() => setIsRedacting(true)}
              >
                <Eraser className="h-4 w-4 mr-1" />
                Redact
              </Button>
              <Button size="sm" onClick={handleUseImage}>
                <Type className="h-4 w-4 mr-1" />
                Use image
              </Button>
              <Button variant="ghost" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
            {isRedacting && (
              <p className="text-xs text-muted-foreground">
                Click and drag on the image to blur sensitive areas, then click &quot;Use image&quot;.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
